/*
 * Minimal Tram shim for udesly-shopify.min.js after webflow.js removal.
 * udesly animates the Webflow Commerce cart modal open/close with
 *   window.Webflow.tram(el).add(spec).set(props).start(props).then(fn).stop()
 * which is webflow.js's Tram engine — gone with webflow.js. Without it the cart
 * open/close throws ("Webflow.tram is not a function") and the modal sticks.
 * This re-implements exactly the API + property keys udesly uses (opacity +
 * x/y/z/rotate/scale transforms) on top of CSS transitions. ES5-safe.
 *
 * CRITICAL: real Tram CANCELS a pending .then() callback (and running tween)
 * when a NEW animation starts — or .stop() is called — on the same element.
 * udesly's cart close does tram(i).start({opacity:0}).then(()=>i.display='none')
 * and the re-open does tram(i).set(...).start(...). If the stale close .then is
 * NOT cancelled, it fires display:none AFTER the cart re-opened → cart hides →
 * udesly re-opens → flicker loop. So set/start/stop/then all cancel any pending
 * callback on that element.
 *
 * Must run BEFORE udesly-shopify.min.js (loaded synchronously in <head>).
 */
(function () {
  'use strict';

  function parseTiming(spec) {
    var p = String(spec).trim().split(/\s+/); // "opacity 200ms ease 0ms"
    return { prop: p[0] || 'all', dur: p[1] || '0ms', ease: p[2] || 'ease', delay: p[3] || '0ms' };
  }

  function durMs(d) {
    var n = parseFloat(d) || 0;
    if (/s$/.test(d) && !/ms$/.test(d)) { n *= 1000; } // "0.3s" -> 300 ; "200ms" -> 200
    return n;
  }

  function buildTransform(s) {
    return 'translate3d(' + (s.x || 0) + 'px,' + (s.y || 0) + 'px,' + (s.z || 0) + 'px)' +
           ' rotate(' + (s.rotate || 0) + 'deg)' +
           ' scale(' + (s.scale == null ? 1 : s.scale) + ')';
  }

  function applyProps(el, props) {
    var s = el.__tramState, touchTransform = false;
    for (var k in props) {
      if (!props.hasOwnProperty(k)) { continue; }
      if (k === 'opacity') {
        el.style.opacity = props[k];
      } else if (k === 'x' || k === 'y' || k === 'z' || k === 'rotate' || k === 'scale') {
        s[k] = props[k];
        touchTransform = true;
      }
    }
    if (touchTransform) {
      var t = buildTransform(s);
      el.style.webkitTransform = t;
      el.style.transform = t;
    }
  }

  /* Cancel any pending .then() callback (its timeout + transitionend) on el. */
  function cancelThen(el) {
    var st = el.__tramThen;
    if (st) {
      clearTimeout(st.timer);
      el.removeEventListener('transitionend', st.onEnd);
      el.__tramThen = null;
    }
  }

  function Tram(el) {
    this.el = el;
    if (!el.__tramState) { el.__tramState = { x: 0, y: 0, z: 0, rotate: 0, scale: 1 }; }
    this._specs = [];
    this._dur = 0;
  }

  Tram.prototype.add = function (spec) {
    this._specs.push(parseTiming(spec));
    return this;
  };

  Tram.prototype.set = function (props) {
    var el = this.el;
    cancelThen(el); // an instant set supersedes any pending callback
    var prev = el.style.transition;
    el.style.transition = 'none';
    applyProps(el, props);
    void el.offsetHeight; // force reflow so the set is committed
    el.style.transition = prev;
    return this;
  };

  Tram.prototype.start = function (props) {
    var el = this.el;
    cancelThen(el); // a NEW animation supersedes any pending callback (no stale display:none)
    if (this._specs.length) {
      var parts = [];
      this._dur = 0;
      for (var i = 0; i < this._specs.length; i++) {
        var t = this._specs[i];
        parts.push(t.prop + ' ' + t.dur + ' ' + t.ease + ' ' + t.delay);
        this._dur = Math.max(this._dur, durMs(t.dur) + durMs(t.delay));
      }
      el.style.transition = parts.join(', ');
      void el.offsetHeight; // ensure transition is in effect before changing props
    }
    applyProps(el, props);
    return this;
  };

  Tram.prototype.then = function (fn) {
    var el = this.el, self = this;
    cancelThen(el); // only the latest then is active
    var st = { done: false };
    function fire() {
      if (st.done) { return; }
      st.done = true;
      el.removeEventListener('transitionend', st.onEnd);
      clearTimeout(st.timer);
      if (el.__tramThen === st) { el.__tramThen = null; }
      try { fn.call(self); } catch (e) { /* ignore */ }
    }
    st.onEnd = function (e) { if (e.target === el) { fire(); } };
    st.timer = setTimeout(fire, (this._dur || 300) + 80); // fallback if transitionend never fires
    el.addEventListener('transitionend', st.onEnd);
    el.__tramThen = st;
    return this;
  };

  Tram.prototype.stop = function () {
    cancelThen(this.el); // stop cancels the pending callback (real tram behaviour)
    return this;
  };

  window.Webflow = window.Webflow || [];
  window.Webflow.tram = function (el) { return new Tram(el); };
}());
